import { TextInput, Textarea, Stack, Switch, NumberInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useEffect } from "react";
import { CptCode } from "../../../services/admin/cptcodes.service";
import {
  required,
  compose,
  maxLength,
  scrollToFirstError,
} from "../../../utils/formValidation";

export interface CptCodeFormValues {
  code: string;
  description: string;
  isActive: boolean;
  fee: number | null;
}

interface CptCodeFormProps {
  initialValues?: CptCode;
  onSubmit: (values: CptCodeFormValues) => void;
}

const toFee = (value: CptCode["fee"]): number | null => {
  if (value === null || value === undefined) return null;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
};

export default function CptCodeForm({
  initialValues,
  onSubmit,
}: CptCodeFormProps) {
  const form = useForm<CptCodeFormValues>({
    initialValues: {
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      isActive: initialValues?.isActive ?? true,
      fee: toFee(initialValues?.fee ?? null),
    },
    validate: {
      code: compose(
        required("CPT Code is required"),
        maxLength(10, "CPT Code must be 10 characters or less")
      ),
      description: required("Description is required"),
    },
  });

  // Reset form when initialValues change (switching between add/edit)
  useEffect(() => {
    form.setValues({
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      isActive: initialValues?.isActive ?? true,
      fee: toFee(initialValues?.fee ?? null),
    });
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleSubmit = useCallback(
    (values: CptCodeFormValues) => {
      onSubmit({
        ...values,
        fee:
          values.fee === null || values.fee === undefined
            ? null
            : Number(values.fee),
      });
    },
    [onSubmit]
  );

  return (
    <form
      id="cpt-code-form"
      noValidate
      onSubmit={form.onSubmit(handleSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="CPT Code"
          placeholder="e.g., 88305"
          withAsterisk
          {...form.getInputProps("code")}
        />

        <Textarea
          label="Description"
          placeholder="Enter CPT code description"
          minRows={3}
          withAsterisk
          {...form.getInputProps("description")}
        />

        <NumberInput
          label="Fee"
          description="Used by the Billing dashboard for revenue tracking. Leave blank if the fee has not been set."
          placeholder="0.00"
          prefix="$"
          min={0}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          allowNegative={false}
          hideControls
          {...form.getInputProps("fee")}
        />

        <Switch
          label="Active"
          description="Inactive codes will not be available for selection"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
